import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  LayoutDashboard,
  Ticket,
  Users,
  MapPin,
  Video,
  Monitor,
  Settings,
  DollarSign,
  LogOut,
  Menu,
  X,
  ChevronDown,
  User,
  Bell,
  Package,
  FileText,
  Clock,
  Truck,
  ShoppingCart,
  BarChart3,
  Globe,
  Shield,
  Key,
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { languageNames } from "../i18n/translations";

const SAG_LOGO = "/sag-logo.png";

const getNavItems = (role, t) => {
  const baseItems = [
    { label: t("nav.dashboard"), icon: LayoutDashboard, path: `/${role === "field_agent" ? "field-agent" : role}` },
  ];

  if (role === "admin") {
    return [
      ...baseItems,
      { label: t("nav.supportQueue"), icon: Ticket, path: "/consultant" },
      { label: t("nav.workOrders"), icon: Truck, path: "/admin/work-orders" },
      { label: t("nav.fieldService"), icon: MapPin, path: "/field-agent" },
      { label: t("nav.slaContracts"), icon: FileText, path: "/admin/sla-contracts" },
      { label: t("nav.timeTracking"), icon: Clock, path: "/time-tracking" },
      { label: t("nav.serviceCatalog"), icon: ShoppingCart, path: "/admin/services" },
      { label: t("nav.earnings"), icon: BarChart3, path: "/earnings" },
      { label: t("nav.invoices") || "Invoices", icon: FileText, path: "/admin/invoices" },
      { label: "Agreements", icon: Shield, path: "/admin/agreements" },
      { label: t("nav.products"), icon: Package, path: "/admin/products" },
      { label: t("nav.userManagement"), icon: Users, path: "/admin/users" },
      { label: t("nav.quickbooks"), icon: DollarSign, path: "/admin/quickbooks" },
      { label: "Payment Settings", icon: Key, path: "/admin/payment-settings" },
    ];
  }

  if (role === "consultant") {
    return [
      ...baseItems,
      { label: t("nav.supportQueue"), icon: Ticket, path: "/consultant" },
      { label: t("nav.timeTracking"), icon: Clock, path: "/time-tracking" },
      { label: t("nav.myEarnings"), icon: BarChart3, path: "/earnings" },
    ];
  }

  if (role === "field_agent") {
    return [
      ...baseItems,
      { label: t("nav.myJobs"), icon: MapPin, path: "/field-agent" },
    ];
  }

  // Client
  return [
    ...baseItems,
    { label: t("nav.myTickets"), icon: Ticket, path: "/client" },
    { label: t("nav.services"), icon: ShoppingCart, path: "/services" },
    { label: t("nav.myInvoices") || "My Invoices", icon: FileText, path: "/client/invoices" },
    { label: t("nav.paymentHistory"), icon: DollarSign, path: "/earnings" },
  ];
};

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();

  const navItems = getNavItems(user?.role, t);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const NavLink = ({ item }) => {
    const isActive = location.pathname === item.path;
    return (
      <Link
        to={item.path}
        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
          isActive
            ? "bg-sag-navy text-white"
            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
        }`}
        onClick={() => setSidebarOpen(false)}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
      >
        <item.icon className="h-5 w-5" />
        <span className="font-medium">{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 h-16 bg-white border-b border-slate-200 px-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-menu-toggle"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <img src={SAG_LOGO} alt="SAG" className="h-8 w-8 object-contain" />
          <span className="font-heading font-bold text-sag-navy">SAG Helpdesk</span>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-full">
              <div className="w-8 h-8 rounded-full bg-sag-navy flex items-center justify-center text-white font-bold">
                {user?.full_name?.charAt(0) || "U"}
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="font-medium">{user?.full_name}</span>
                <span className="text-xs text-slate-500 capitalize">{user?.role?.replace("_", " ")}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-red-600">
              <LogOut className="h-4 w-4 mr-2" />
              {t("common.signOut")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 lg:z-30 h-full w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-in-out ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        {/* Logo */}
        <div className="h-16 px-6 flex items-center gap-3 border-b border-slate-100">
          <img src={SAG_LOGO} alt="SAG" className="h-10 w-10 object-contain" />
          <div>
            <span className="font-heading font-bold text-sag-navy block leading-tight">SAG</span>
            <span className="text-xs text-slate-500">Helpdesk</span>
          </div>
        </div>

        {/* Navigation */}
        <ScrollArea className="h-[calc(100vh-4rem-5rem)]">
          <nav className="p-4 space-y-1">
            {navItems.map((item) => (
              <NavLink key={item.label} item={item} />
            ))}
          </nav>
        </ScrollArea>

        {/* User Section */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-slate-100 bg-white">
          {/* Language Switcher */}
          <div className="px-4 pt-3 pb-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="w-full justify-between text-slate-600 h-8" data-testid="language-switcher">
                  <span className="flex items-center gap-2">
                    <Globe className="h-3.5 w-3.5" />
                    <span className="text-xs">{languageNames[language]}</span>
                  </span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                <DropdownMenuLabel className="text-xs">{t("common.language")}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {Object.entries(languageNames).map(([code, name]) => (
                  <DropdownMenuItem key={code} onClick={() => setLanguage(code)} className={language === code ? "bg-slate-100 font-medium" : ""}>
                    {name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="px-4 pb-4 pt-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full justify-start gap-3 h-auto py-2">
                  <div className="w-10 h-10 rounded-full bg-sag-navy flex items-center justify-center text-white font-bold flex-shrink-0">
                    {user?.full_name?.charAt(0) || "U"}
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className="font-medium text-slate-900 truncate">{user?.full_name}</p>
                    <p className="text-xs text-slate-500 capitalize">{user?.role?.replace("_", " ")}</p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{t("common.myAccount")}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="h-4 w-4 mr-2" />
                  {t("common.signOut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        {/* Desktop Header */}
        <header className="hidden lg:flex h-16 bg-white border-b border-slate-200 px-8 items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="font-heading text-lg font-semibold text-slate-900">
              {navItems.find((item) => item.path === location.pathname)?.label || "Dashboard"}
            </h2>
          </div>
          <div className="flex items-center gap-4">
            {/* Desktop Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1.5 text-slate-600" data-testid="desktop-language-switcher">
                  <Globe className="h-4 w-4" />
                  <span className="text-sm">{languageNames[language]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {Object.entries(languageNames).map(([code, name]) => (
                  <DropdownMenuItem key={code} onClick={() => setLanguage(code)} className={language === code ? "bg-slate-100 font-medium" : ""}>
                    {name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="h-5 w-5 text-slate-500" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2">
                  <div className="w-8 h-8 rounded-full bg-sag-navy flex items-center justify-center text-white font-bold">
                    {user?.full_name?.charAt(0) || "U"}
                  </div>
                  <span className="font-medium">{user?.full_name}</span>
                  <ChevronDown className="h-4 w-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-medium">{user?.full_name}</span>
                    <span className="text-xs text-slate-500">{user?.email}</span>
                    <span className="text-xs text-slate-400 capitalize mt-1">{user?.role?.replace("_", " ")}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="h-4 w-4 mr-2" />
                  {t("common.signOut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
